import axios from 'axios';
import { tokenUtils } from '../utils/tokenUtils';

const API_URL = process.env.REACT_APP_API_URL + '/UserQuestionnaireResult';

function getAuthHeaders() {
  const token = tokenUtils.get();
  if (!token) throw new Error('No se encontró token de autenticación');
  return {
    Authorization: `Bearer ${token}`,
  };
}

const handleAuthError = (error) => {
  if (error.response?.status === 401) {
    tokenUtils.clearAuth();
    window.location.href = '/';
  }
};

// Get leaderboard by average score (accuracy)
const getAccuracyLeaderboardData = async (filters = {}) => {
  try {
    const { top = 50, page = 1, limit = 20 } = filters;
    const response = await axios.get(`${API_URL}/leaderboard/average-score?top=${top}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener leaderboard de precisión:', error);
    throw error;
  }
};

// Get leaderboard by best score
const getBestScoreLeaderboardData = async (filters = {}) => {
  try {
    const { top = 50 } = filters;
    const response = await axios.get(`${API_URL}/leaderboard/best-score?top=${top}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener leaderboard de mejor puntuación:', error);
    throw error;
  }
};

// Get leaderboard by improvement rate
const getImprovementRateLeaderboardData = async (filters = {}) => {
  try {
    const { top = 50 } = filters;
    const response = await axios.get(`${API_URL}/leaderboard/improvement-rate?top=${top}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener leaderboard de mejora:', error);
    throw error;
  }
};

// Get leaderboard by completion rate
const getCompletionRateLeaderboardData = async (filters = {}) => {
  try {
    const { top = 50 } = filters;
    const response = await axios.get(`${API_URL}/leaderboard/completion-rate?top=${top}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener leaderboard de completitud:', error);
    throw error;
  }
};

// Get leaderboard by total attempts
const getTotalAttemptsLeaderboardData = async (filters = {}) => {
  try {
    const { top = 50 } = filters;
    const response = await axios.get(`${API_URL}/leaderboard/total-attempts?top=${top}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener leaderboard de intentos totales:', error);
    throw error;
  }
};

// Get leaderboard by streak days
const getStreakDaysLeaderboardData = async (filters = {}) => {
  try {
    const { top = 50 } = filters;
    const response = await axios.get(`${API_URL}/leaderboard/streak-days?top=${top}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener leaderboard de días consecutivos:', error);
    throw error;
  }
};

// Get leaderboard by recent activity
const getRecentActivityLeaderboardData = async (filters = {}) => {
  try {
    const { days = 30, top = 50 } = filters;
    const response = await axios.get(`${API_URL}/leaderboard/recent-activity?days=${days}&top=${top}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener leaderboard de actividad reciente:', error);
    throw error;
  }
};

// Get leaderboard summary
const getLeaderboardSummary = async () => {
  try {
    const response = await axios.get(`${API_URL}/leaderboard/summary`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener resumen del leaderboard:', error);
    throw error;
  }
};

// Get user ranking
const getUserRanking = async (userId, metric = 'AverageScore') => {
  try {
    const response = await axios.get(`${API_URL}/user/${userId}/ranking?metric=${metric}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener ranking del usuario:', error);
    throw error;
  }
};

// Get global statistics
const getGlobalStatistics = async () => {
  try {
    const response = await axios.get(`${API_URL}/global-statistics`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener estadísticas globales:', error);
    throw error;
  }
};

// Get user's personal statistics (updated to use performance summary)
const getUserLeaderboardStats = async (userId) => {
  try {
    const response = await axios.get(`${API_URL}/user/${userId}/performance-summary`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener estadísticas del usuario:', error);
    throw error;
  }
};

// Get leaderboard analytics (using global statistics)
const getLeaderboardAnalytics = async () => {
  try {
    const response = await axios.get(`${API_URL}/global-statistics`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener analytics del leaderboard:', error);
    throw error;
  }
};

// Get all questionnaires for leaderboard selection
const getQuestionnairesForLeaderboard = async () => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/Questionnaire`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener cuestionarios para leaderboard:', error);
    throw error;
  }
};

// Get questionnaire-specific leaderboard data (using best score leaderboard)
const getQuestionnaireLeaderboardData = async (questionnaireId, page = 1, limit = 20) => {
  try {
    // Since there's no questionnaire-specific endpoint, we'll use the best score leaderboard
    // and filter by questionnaire if needed
    const response = await axios.get(`${API_URL}/leaderboard/best-score?top=50`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener datos del leaderboard:', error);
    throw error;
  }
};

// Export leaderboard data
const exportLeaderboardData = async (questionnaireId = null, format = 'csv') => {
  try {
    const url = questionnaireId 
      ? `${API_URL}/questionnaire/${questionnaireId}/export?format=${format}`
      : `${API_URL}/export?format=${format}`;
    
    const response = await axios.get(url, {
      headers: getAuthHeaders(),
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al exportar datos del leaderboard:', error);
    throw error;
  }
};

// Get real-time leaderboard updates
const subscribeToLeaderboardUpdates = (questionnaireId, callback) => {
  // This would integrate with SignalR for real-time updates
  // Implementation depends on your SignalR setup
  console.log('Subscribing to leaderboard updates for questionnaire:', questionnaireId);
  return () => {
    console.log('Unsubscribing from leaderboard updates');
  };
};

const leaderboardService = {
  getQuestionnairesForLeaderboard,
  getQuestionnaireLeaderboardData,
  getAccuracyLeaderboardData,
  getBestScoreLeaderboardData,
  getImprovementRateLeaderboardData,
  getCompletionRateLeaderboardData,
  getTotalAttemptsLeaderboardData,
  getStreakDaysLeaderboardData,
  getRecentActivityLeaderboardData,
  getLeaderboardSummary,
  getUserRanking,
  getGlobalStatistics,
  getUserLeaderboardStats,
  getLeaderboardAnalytics,
  exportLeaderboardData,
  subscribeToLeaderboardUpdates,
};

export default leaderboardService; 